import { otpInputStyles } from '~/components/OTPCodeModal/styles.ts';
import OutlinedInput from '@mui/material/OutlinedInput';
import React, { ChangeEvent, KeyboardEvent, useRef } from 'react';
import { theme } from '~/theme';
import { Grid } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useFormContext, useWatch } from 'react-hook-form';

interface OtpInputFieldProps {
  error?: string | boolean;
}

type TOtpFormValues = {
  otp: string[];
};

export const OtpInputField = ({ error }: OtpInputFieldProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { control, setValue } = useFormContext<TOtpFormValues>();
  const otp = useWatch({ control, name: 'otp' }) ?? [];

  const updateOtp = (newOtp: string[]) => {
    setValue('otp', newOtp, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const value = e.target.value;

    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, otp.length);
      if (digits.length > 0) {
        const newOtp = [...otp];
        for (let i = 0; i < digits.length && index + i < otp.length; i++) {
          newOtp[index + i] = digits[i] || '';
        }
        updateOtp(newOtp);
        const nextIndex = Math.min(index + digits.length, otp.length - 1);
        inputRefs.current[nextIndex]?.focus();
      }
      return;
    }

    if (!/^[0-9]$/.test(value) && value !== '') return;

    const newOtp = [...otp];
    newOtp[index] = value;
    updateOtp(newOtp);

    if (index < otp.length - 1 && value !== '') {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otp];
      if (!otp[index] && index > 0) {
        newOtp[index - 1] = '';
        updateOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      } else {
        newOtp[index] = '';
        updateOtp(newOtp);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, otp.length);

    if (digits.length > 0) {
      const newOtp = digits.split('').concat(new Array(otp.length).fill('')).slice(0, otp.length);
      updateOtp(newOtp);
      const nextIndex = Math.min(digits.length, otp.length - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <Grid sx={{ mt: '20px', mb: '20px' }}>
      <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} sx={{ justifyContent: 'center' }}>
        {otp.map((otpChar, index) => (
          <OutlinedInput
            key={index}
            sx={otpInputStyles(theme)}
            error={Boolean(error)}
            inputRef={(el) => (inputRefs.current[index] = el)}
            value={otpChar}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            inputProps={{
              maxLength: 1,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              autoComplete: index === 0 ? 'one-time-code' : 'off',
              sx: {
                textAlign: 'center',
                padding: 0,
              },
            }}
          />
        ))}
      </Stack>
    </Grid>
  );
};
